import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Sparkles,
  Utensils,
  Calendar,
  CheckCircle,
  Phone,
  Mail
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

import {
  FadeIn,
  StaggerContainer,
  StaggerItem
} from '../components/PageTransition';

import { useNavigate } from 'react-router-dom';

import {
  BengaliDecorativeElement,
  BengaliCornerDecor,
  BengaliDivider
} from '../components/BengaliPatterns';

import banquet from "../assets/Banquit.png";

const BanquetsPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Users,
      title: "Wedding Catering",
      description:
        "Luxury Bengali wedding catering with authentic traditional cuisine.",
      image:
        "https://images.unsplash.com/photo-1519167758481-83f550bb49b3"
    },
    {
      icon: Sparkles,
      title: "Corporate Events",
      description:
        "Premium catering solutions for corporate meetings & events.",
      image:
        "https://images.unsplash.com/photo-1555244162-803834f70033"
    },
    {
      icon: Utensils,
      title: "Private Celebrations",
      description:
        "Celebrate birthdays & anniversaries with authentic Bengali flavors.",
      image:
        "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3"
    }
  ];

  const dishes = [
    "Shorshe Ilish",
    "Kosha Mangsho",
    "Chingri Malai Curry",
    "Bengali Thali",
    "Seasonal Bengali Sweets",
    "Vegetarian Special Menu"
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* HERO SECTION */}

      <section className="relative h-screen flex items-center overflow-hidden" style={{marginTop:"123px"}}>

        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <img
            src={banquet}
            alt="Banquet"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30"></div>
        </motion.div>

        <div className="container mx-auto px-6 relative z-10">

          <motion.div
            style={{ marginTop: '110px' }}
            className="max-w-3xl sm:mt-[8rem]"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              Celebrate with Our{" "}
              <span className="text-amber-400">
                Banquet & Catering
              </span>
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-10">
              Experience authentic Bengali cuisine crafted for weddings,
              anniversaries, birthdays, and corporate gatherings with
              unmatched hospitality and elegant presentation.
            </p>

            <div className="flex flex-wrap gap-5">

              <Button
                onClick={() => navigate("/contact")}
                className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-6 text-lg rounded-xl"
              >
                <Mail className="mr-2 w-5 h-5" />
                Book Event
              </Button>

              <Button
                onClick={() => navigate("/menu")}
                className="bg-white/10 backdrop-blur-md border border-white/40 hover:bg-white/20 text-white px-8 py-6 text-lg rounded-xl"
              >
                <Phone className="mr-2 w-5 h-5" />
                Explore Menu
              </Button>

            </div>

          </motion.div>

        </div>
      </section>

      {/* SERVICES */}

      <section className="py-24 bg-white relative overflow-hidden">

        <BengaliDecorativeElement pattern="paisley" />
        <BengaliCornerDecor />

        <div className="container mx-auto px-6 relative z-10">

          <FadeIn>
            <div className="text-center mb-16">

              <h2 className="text-5xl font-bold text-amber-900 mb-5">
                Our Catering Services
              </h2>

              <BengaliDivider />

              <p className="text-gray-600 text-lg max-w-2xl mx-auto mt-5">
                Elegant catering services designed for every special occasion.
              </p>

            </div>
          </FadeIn>

          <StaggerContainer staggerDelay={0.2}>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

              {services.map((service, index) => (

                <StaggerItem key={index}>

                  <motion.div
                    whileHover={{ y: -10 }}
                    transition={{ duration: 0.3 }}
                  >

                    <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition duration-500 rounded-3xl">

                      <div className="relative h-72 overflow-hidden">

                        <motion.img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

                        <div className="absolute bottom-5 left-5">
                          <service.icon className="w-10 h-10 text-amber-400 mb-3" />
                          <h3 className="text-3xl font-bold text-white">
                            {service.title}
                          </h3>
                        </div>

                      </div>

                      <CardContent className="p-6">

                        <p className="text-gray-600 leading-relaxed">
                          {service.description}
                        </p>

                      </CardContent>

                    </Card>

                  </motion.div>

                </StaggerItem>

              ))}

            </div>

          </StaggerContainer>

        </div>
      </section>

      {/* SPECIAL DISHES */}

      <section className="py-24 bg-gradient-to-b from-amber-50 to-white relative">

        <BengaliDecorativeElement
          pattern="lotus"
          className="opacity-20"
        />

        <div className="container mx-auto px-6 relative z-10">

          <div className="text-center mb-16">

            <h2 className="text-5xl font-bold text-amber-900 mb-5">
              Signature Bengali Dishes
            </h2>

            <BengaliDivider />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {dishes.map((dish, index) => (

              <motion.div
                key={index}
                whileHover={{ scale: 1.03 }}
                className="bg-white rounded-2xl shadow-lg p-6 flex items-center gap-4"
              >

                <CheckCircle className="text-amber-700 w-7 h-7" />

                <p className="text-lg font-semibold text-gray-700">
                  {dish}
                </p>

              </motion.div>

            ))}

          </div>

        </div>

      </section>

      {/* CTA */}

      <section className="py-24 bg-amber-900 relative overflow-hidden">

        <BengaliDecorativeElement
          pattern="conch"
          className="opacity-10"
        />

        <div className="container mx-auto px-6 text-center relative z-10">

          <h2 className="text-5xl font-bold text-white mb-6">
            Make Your Celebration Unforgettable
          </h2>

          <p className="text-gray-200 text-lg max-w-3xl mx-auto mb-10">
            From intimate family gatherings to grand Bengali weddings,
            we create unforgettable dining experiences filled with
            authentic flavors and premium hospitality.
          </p>

          <Button
            onClick={() => navigate("/contact")}
            className="bg-amber-700 hover:bg-amber-800 text-white px-10 py-6 text-lg rounded-xl"
          >
            <Calendar className="mr-2 w-5 h-5" />
            Book Consultation
          </Button>

        </div>

      </section>

    </div>
  );
};

export default BanquetsPage;